import { useState } from 'react';
import {
	assignToLegacyReferralOptions,
	ClinicalTypeEnum,
	DisciplineEnum,
	getLegacyReferralOptions,
	LegacyReferralNotificationStatusEnum,
	LegacyReferralStatusEnum,
	type LegacyReferralNotificationViewModel,
	type LegacyReferralViewModel
} from '@generated';
import { useLegacyReferralContext } from '../LegacyReferralContext';
import { useMutation } from '~/util/http/useMutation';
import { getSearchQueryKey } from '~/util/http/useSearch';
import { Modal } from '~/shared/components/modal/Modal';
import { DataTable } from '~/shared/data-tables/DataTable';
import { StatusPill } from '~/shared/StatusPill';
import type { DataTableColumnProps } from '~/shared/data-tables/DataTableColumn';
import { TableActions } from '~/shared/data-tables/TableActions';
import { formatToShortDateAndTimeOrNull } from '~/util/date';
import {
	getDisciplineIdForRole,
	getMessageStatusIntent,
	getMessageStatusLabel,
	getNewReferralMessage,
	getNotificationStatusLabel,
	getResponseIntent,
	getResponseLabel,
	getRoleAbbreviation
} from '../referralHelpers';

function getNonSnAssignment(referral: LegacyReferralViewModel, disciplineId: number) {
	switch (disciplineId) {
		case DisciplineEnum.PhysicalTherapy:
			return referral.ptAssign;
		case DisciplineEnum.OccupationalTherapy:
			return referral.otAssign;
		case DisciplineEnum.SpeechTherapy:
			return referral.stAssign;
		case DisciplineEnum.MedicalSocialWorker:
			return referral.mswAssign;
		case DisciplineEnum.HomeHealthAide:
			return referral.hhaAssign;
		default:
			return undefined;
	}
}

function TeamsMessageCell({ notification }: { notification: LegacyReferralNotificationViewModel }) {
	const { referral } = useLegacyReferralContext();
	const message = getNewReferralMessage(referral.messages, notification.userId);
	if (!message) {
		return <StatusPill intent="neutral">Not Sent</StatusPill>;
	}
	return (
		<div className="flex flex-col gap-1">
			<StatusPill intent={getMessageStatusIntent(message.statusId)}>{getMessageStatusLabel(message.statusId)}</StatusPill>
			{message.errorMessage && <span className="text-xs text-red-700">{message.errorMessage}</span>}
		</div>
	);
}

function RenderActionsCell(props: LegacyReferralNotificationViewModel) {
	const { referral } = useLegacyReferralContext();
	const assignToLegacyReferral = useMutation({
		...assignToLegacyReferralOptions,
		invalidateQueryKeys: [getLegacyReferralOptions(referral.id).queryKey, getSearchQueryKey('legacy-referral')]
	});
	const [displayAssignModal, setDisplayAssignModal] = useState(false);

	if (referral.statusId === LegacyReferralStatusEnum.Ntfc || props.statusId !== LegacyReferralNotificationStatusEnum.InProgress) {
		return null;
	}

	const disciplineId = getDisciplineIdForRole(props.roleId);
	if (!referral.disciplinesNeeded.includes(disciplineId)) {
		return null;
	}

	const handleAssign = (startOfCare: boolean, primaryCare: boolean) => {
		assignToLegacyReferral.mutate(
			{
				referralId: referral.id,
				disciplineId,
				userId: props.userId,
				startOfCare,
				primaryCare
			},
			{
				onSettled: () => setDisplayAssignModal(false)
			}
		);
	};

	if (disciplineId !== DisciplineEnum.SkilledNursing) {
		if (getNonSnAssignment(referral, disciplineId)?.assignedToId === props.userId) {
			return null;
		}
		return (
			<TableActions>
				<TableActions.AddButton aria-label="Assign" onClick={() => handleAssign(false, false)} isPending={assignToLegacyReferral.isPending} />
			</TableActions>
		);
	}

	const assignedStart = referral.snStartAssign?.assignedToId === props.userId;
	const assignedPrimary = referral.snPrimaryCareAssign?.assignedToId === props.userId;
	const isRn = props.roleId === ClinicalTypeEnum.RN;

	if (assignedPrimary && (assignedStart || !isRn)) {
		return null;
	}
	if (!isRn) {
		return (
			<TableActions>
				<TableActions.AddButton aria-label="Assign" onClick={() => handleAssign(false, true)} isPending={assignToLegacyReferral.isPending} />
			</TableActions>
		);
	}

	return (
		<TableActions>
			<TableActions.AddButton aria-label="Assign" onClick={() => setDisplayAssignModal(true)} />
			{displayAssignModal && (
				<Modal title="Assign" closeModal={() => setDisplayAssignModal(false)}>
					<p className="text-sm text-gray-700">Which assignment type?</p>
					<div className="mt-6 flex justify-end gap-2">
						{!assignedStart && !assignedPrimary && (
							<TableActions.AddButton
								autoFocus
								onClick={() => handleAssign(true, true)}
								label="Both"
								isPending={assignToLegacyReferral.isPending}
							/>
						)}
						{!assignedStart && (
							<TableActions.AddButton
								intent="warning"
								onClick={() => handleAssign(true, false)}
								label="SOC"
								isPending={assignToLegacyReferral.isPending}
							/>
						)}
						{!assignedPrimary && (
							<TableActions.AddButton
								intent="warning"
								onClick={() => handleAssign(false, true)}
								label="Primary Care"
								isPending={assignToLegacyReferral.isPending}
							/>
						)}
					</div>
				</Modal>
			)}
		</TableActions>
	);
}

const columns: DataTableColumnProps<LegacyReferralNotificationViewModel>[] = [
	{
		header: 'Clinician',
		key: 'user',
		renderCell: x => (
			<div className="flex flex-col">
				<span>{x.user.fullName}</span>
				<span className="text-xs text-gray-500">{x.user.emailAddress}</span>
			</div>
		)
	},
	{
		header: 'Role',
		key: 'roleId',
		renderCell: x => getRoleAbbreviation(x.roleId)
	},
	{
		header: 'Distance',
		key: 'distance',
		renderCell: x => (typeof x.distance === 'number' ? `${x.distance.toFixed(1)} mi` : '-')
	},
	{
		header: 'Teams',
		key: 'teams',
		renderCell: x => <TeamsMessageCell notification={x} />
	},
	{
		header: 'Status',
		key: 'statusId',
		renderCell: x => (
			<StatusPill intent={x.statusId === LegacyReferralNotificationStatusEnum.InProgress ? 'info' : 'neutral'}>
				{getNotificationStatusLabel(x.statusId)}
			</StatusPill>
		)
	},
	{
		header: 'Response',
		key: 'accepted',
		renderCell: x => (
			<div className="flex flex-col gap-1">
				<StatusPill intent={getResponseIntent(x)}>{getResponseLabel(x)}</StatusPill>
				{x.responseComments && <span className="text-xs text-gray-600">{x.responseComments}</span>}
			</div>
		)
	},
	{
		header: 'Resp At',
		key: 'respondedOn',
		renderCell: x => formatToShortDateAndTimeOrNull(x.respondedOn)
	},
	{
		key: 'actions',
		header: 'Actions',
		width: 'w-24',
		renderCell: x => <RenderActionsCell {...x} />
	}
];

export function LegacyReferralNotificationsTable({ rows }: { rows: LegacyReferralNotificationViewModel[] }) {
	return <DataTable columns={columns} rows={rows} />;
}
